// src/Context/RoleContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthApi } from '../Api/BaseApi';

const RoleContext = createContext();

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user'
};

// Permission constants
export const PERMISSIONS = {
  CREATE_USER: 'create_user',
  DELETE_USER: 'delete_user',
  MANAGE_USERS: 'manage_users',
  VIEW_ADMIN_PANEL: 'view_admin_panel'
};

// Map a backend role string ("Admin", "AR Analyst", "CFO/Controller", …) to the
// internal role token. Only an exact "Admin" (case-insensitive) is an admin.
export const normalizeRole = (serverRole) =>
  (String(serverRole || '').trim().toLowerCase() === 'admin') ? ROLES.ADMIN : ROLES.USER;

// Role-based permissions mapping
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    PERMISSIONS.CREATE_USER,
    PERMISSIONS.DELETE_USER,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_ADMIN_PANEL
  ],
  [ROLES.USER]: []
};

export const RoleProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Securely fetch role from server (decoded from HTTP-only cookies)
  const fetchRoleFromServer = async () => {
    setIsLoading(true);
    try {
      const data = await AuthApi.getUserPermissions(); // { role, permissions, first_name, email, ... }
      // The backend role is a display string ("Admin", "AR Analyst", ...). Map it
      // to the internal token: only "Admin" grants the admin role; everything else
      // is a standard user. (Previously compared a display string to 'admin',
      // which silently disabled all admin detection.)
      const role = normalizeRole(data?.role);
      setUserRole(role);
      setUserProfile(data || null);
      setPermissions(ROLE_PERMISSIONS[role] || []);
    } catch (e) {
      // 401 means unauthenticated
      setUserRole(null);
      setUserProfile(null);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return userRole === role;
  };

  // Check if user is admin
  const isAdmin = () => {
    return userRole === ROLES.ADMIN;
  };

  // Check if user is regular user
  const isUser = () => {
    return userRole === ROLES.USER;
  };

  // Update user role and permissions
  const updateUserRole = (newRole) => {
    setUserRole(newRole);
    setPermissions(ROLE_PERMISSIONS[newRole] || []);
  };

  // Refresh user role by querying server
  const refreshUserRole = () => {
    fetchRoleFromServer();
  };

  // On mount: if already authenticated (cookies present), fetch role; otherwise finish loading.
  useEffect(() => {
    const checkAuth = async () => {
      if (AuthApi.isAuthenticated && AuthApi.isAuthenticated()) {
        await fetchRoleFromServer();
      } else {
        // Not authenticated; stop loading so routes can redirect to login
        setIsLoading(false);
      }
    };
    checkAuth();
    return () => {};
  }, []);

  // Reset role on logout
  const resetRole = () => {
    setUserRole(null);
    setUserProfile(null);
    setPermissions([]);
  };

  const rawFirstName = userProfile?.first_name || '';
  const emailFallback = userProfile?.email ? userProfile.email.split('@')[0].split(/[._-]/)[0] : '';
  const candidateName = rawFirstName || emailFallback;
  const firstName = candidateName
    ? (candidateName.charAt(0).toUpperCase() + candidateName.slice(1))
    : '';

  const value = {
    userRole,
    userProfile,
    firstName,
    permissions,
    hasPermission,
    hasRole,
    isAdmin,
    isUser,
    updateUserRole,
    refreshUserRole,
    resetRole,
    ROLES,
    PERMISSIONS,
    isLoading,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
